import {
  useTheme,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Divider,
} from '@mui/material';
import { useEffect, useState } from 'react';
import Feedback from '../../components/special/Feedback';
import { API_DELETE, API_PATCH, DBRequest, useConfig } from '../../api/api';
import { tokens } from '../../global/theme/tokens';
import TextCard from '../../components/cards/TextCard';
import DisplayPositions from '../../components/preview/PreviewPositions';
import FileCard from '../../components/cards/FilesCard';
import DownloadIcon from '@mui/icons-material/Download';
import CardContainer from '../../components/cards/layout/CardContainer';
import { useTranslation } from 'react-i18next';
import SkeletonCard from '../../components/cards/SkeletonCard';
import ExoResendMail from '../../components/exo/ExoResendMail';
import DateLabel from '../../components/label/DateLabel';
import CardGrid from '../../components/cards/layout/CardGrid';
import useFetch from '../../api/useFetch';
import ForwardToInboxIcon from '@mui/icons-material/ForwardToInbox';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ModeEditIcon from '@mui/icons-material/ModeEdit';
import PrintIcon from '@mui/icons-material/Print';
import ShareIcon from '@mui/icons-material/Share';
import SpeedDialMenu from '../../components/menu/SpeedDialMenu';
import NewOrder from '../../components/new/NewOrder';
import PopUpContainer from '../../components/popup/PopUpContainer';
import Share from '../../components/special/Share';
import { ExoAttachments } from '../../components/exo/ExoAttachments';
import moment from 'moment';
import { PositionName } from '../../components/select/SelectPositions';
import StyledTextContainer from '../../components/preview/StyledTextContainer';
import RenderHTML from '../../components/preview/RenderHTML';
import PictureNameCard from '../../components/cards/PictureNameCards/PictureNameCard';
import ProjectCard from '../../components/cards/ProjectCard';
import MailLogCard from '../../components/cards/mailLogCard/MailLogCard';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useNavigate } from 'react-router-dom';
import MoneyLabel from '../../components/label/MoneyLabel';
import HistoryIcon from '@mui/icons-material/History';
import { round } from '../../components/special/round';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import ExoDocumentFromDraft from '../../components/exo/ExoDocumentFromDraft';
import { EditableDocumentStatus } from '../../components/special/EditableDocumentStatus';
import { useAiPageContext } from '../../components/ai-chat';

const ExtendOrder = ({ row, onUpdate }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();
  const config = useConfig();
  const navigate = useNavigate();

  const { apiData, setApiData } = useFetch(`orders/${row.id}`);

  useAiPageContext(
    apiData?.data && {
      type: 'order',
      id: apiData.data.id,
      label: [apiData.data.nr, apiData.data.title].filter(Boolean).join(' · '),
      projectId: apiData.data.project?.id,
      projectLabel: apiData.data.project?.name,
    },
  );

  const [status, setStatus] = useState(0);
  const [open, setOpen] = useState(false);
  const [startData, setStartData] = useState(null);

  const columns = [
    {
      label: t('Position'),
      key: 'position',
      align: 'left',
      hiddenMobile: false,
      type: 'number',
      width: '48px',
    },
    {
      label: t('Description'),
      key: 'name',
      align: 'left',
      hiddenMobile: false,
      type: 'custom',
      width: 'auto',
      cell: (row) => <PositionName element={row} />,
    },
    {
      label: t('Quantity'),
      key: 'amount',
      align: 'center',
      hiddenMobile: false,
      type: 'number',
      width: '80px',
    },
  ];

  const additionalColumns = [
    {
      label: t('Ordered'),
      key: 'ordered',
      align: 'right',
      type: 'number',
      cell: (row) => (
        <Box className="flex justify-end">
          <MoneyLabel money={round(row.amount * row.price)} />
        </Box>
      ),
    },
    {
      label: t('Settled'),
      key: 'billed',
      align: 'right',
      type: 'number',
      cell: (row) => (
        <Box className="flex justify-end">
          <MoneyLabel money={row.billedTotal} />
        </Box>
      ),
    },
    {
      label: t('Open'),
      key: 'open',
      align: 'right',
      type: 'number',
      cell: (row) => (
        <Box className="flex justify-end">
          <MoneyLabel money={round(row.amount * row.price) - row.billedTotal} />
        </Box>
      ),
    },
  ];

  // menu
  const actions = [
    {
      icon: <ModeEditIcon />,
      name: t('Edit'),
      onClick: handleEdit,
    },
    {
      icon: <NoteAddIcon />,
      name: t('Finalize Order'),
      onClick: () => setOpenCreateOrder(true),
      hidden: apiData && apiData.data.isDraft ? false : true, // only show if it's a draft
    },
    {
      icon: <ContentCopyIcon />,
      name: t('Copy'),
      onClick: handleCopy,
    },
    {
      icon: <ForwardToInboxIcon />,
      name: t('Send Again'),
      onClick: () => setOpenResendMail(true),
    },
    {
      icon: <PrintIcon />,
      name: t('Print'),
      onClick: () => handlePrint(apiData && apiData.data.file.temporaryUrl),
      hidden: apiData && apiData.data.file ? false : true,
    },
    {
      icon: <ShareIcon />,
      name: t('Share'),
      onClick: () => setOpenShare(true),
    },
    { icon: <DeleteOutlineIcon />, name: t('Delete'), onClick: handleDelete },
  ];

  function handleEdit() {
    setStartData(apiData.data);
    setOpen(true);
  }
  function handleCopy() {
    const dataCopied = {};

    const posWithoutIds = apiData.data.positions.map(({ id, ...rest }) => rest);

    dataCopied.positions = posWithoutIds;
    dataCopied.note = apiData.data.note;
    dataCopied.title = apiData.data.title;
    dataCopied.startDate = moment().format();
    dataCopied.deliveryDate = moment().format();

    setStartData(dataCopied);
    setOpen(true);
  }
  function handleUpdate(data) {
    setApiData(data);
    onUpdate(data.data);
  }

  // share
  const [openShare, setOpenShare] = useState(false);
  function handleCloseShare() {
    setOpenShare(!openShare);
  }

  // print
  function handlePrint(pdfUrl) {
    // Open a new window or iframe with the PDF URL
    const printWindow = window.open(pdfUrl, '_blank');

    // Wait for the PDF to load
    printWindow.onload = () => {
      // Trigger the print dialog
      printWindow.print();
    };
  }

  // resend mail
  const [openResendMail, setOpenResendMail] = useState(false);

  // Create Order from a Draft
  const [openCreateOrder, setOpenCreateOrder] = useState(false);

  // delete
  function handleDelete() {
    DBRequest({
      config,
      path: `orders/${apiData.data.id}`,
      method: API_DELETE(),
      onFeedback: handleAlertOpen,
    });
  }

  // feedback
  const [alertState, setAlertState] = useState({
    alertOpen: false,
    alertType: 'info',
    alertText: 'test',
  });
  function requestStatusChange(newStatus) {
    DBRequest({
      config,
      path: `orders/${apiData.data.id}/status`,
      method: API_PATCH({ status: newStatus }),
      onFeedback: handleAlertOpen,
      onResponse: () => {
        setApiData({
          ...apiData,
          data: { ...apiData.data, status: newStatus },
        });
        onUpdate({ ...row, status: newStatus });
      },
    });
  }

  function handleAlertOpen(text, type) {
    setAlertState({ alertOpen: true, alertText: text, alertType: type });
  }

  return (
    <Box className="flex flex-col gap-4 relative overflow-y-auto">
      {!apiData ? (
        <OrderSkeleton />
      ) : (
        <>
          <CardGrid>
            <ExoDocumentFromDraft
              dataCollection={apiData.data}
              recipient={apiData.data.recipient}
              type="orders"
              id={apiData.data.id}
              open={openCreateOrder}
              onClose={() => setOpenCreateOrder(false)}
              handleUpdate={handleUpdate}
              previewDocument={'order'}
            />
            <ExoResendMail
              recipient={apiData.data.recipient}
              type="orders"
              id={apiData.data.id}
              open={openResendMail}
              onClose={() => setOpenResendMail(false)}
            />
            <SpeedDialMenu actions={actions} />
            <TextCard text={apiData.data.title} span={99} />
            <PictureNameCard
              data={apiData.data.recipient}
              type={apiData.data.recipientType}
              title={t('Recipient')}
              pictureUrl={
                apiData.data.recipient.logo &&
                apiData.data.recipient.logo.thumbnailUrl
              }
              name={
                apiData.data.recipient.firstName
                  ? `${apiData.data.recipient.firstName} ${apiData.data.recipient.lastName}`
                  : apiData.data.recipient.name
              }
              colorPool={apiData.data.colorPool}
            />
            <ProjectCard project={apiData.data.project} />
            <CardContainer>
              <Box className="flex flex-col justify-center items-center gap-2 p-4 rounded-lg">
                <Box className="flex flex-row gap-2">
                  <Typography variant="p">{t('Order Nr')}</Typography>
                  <Typography variant="h5" className=" monospace">
                    {apiData.data.nr ?? t('Draft')}
                  </Typography>
                </Box>
                <Box className="flex flex-row gap-2 items-center">
                  <Typography variant="p">{t('Status')}</Typography>
                  <EditableDocumentStatus
                    type="order"
                    status={apiData.data.status}
                    onChange={requestStatusChange}
                  />
                </Box>
                <Box className="flex flex-row gap-2">
                  <Typography variant="p">{t('Start Date')}</Typography>
                  <Typography variant="h5" className="monospace">
                    <DateLabel date={apiData.data.startDate} />
                  </Typography>
                </Box>
                <Box className="flex flex-row gap-2">
                  <Typography variant="p">{t('Delivery Date')}</Typography>
                  <Typography variant="h5" className="monospace">
                    <DateLabel date={apiData.data.deliveryDate} />
                  </Typography>
                </Box>
              </Box>
            </CardContainer>

            <FileCard
              files={[
                {
                  label: t('Order'),
                  icon: <DownloadIcon />,
                  url: apiData.data.file && apiData.data.file.temporaryUrl,
                },
              ]}
            />

            <PictureNameCard
              data={apiData.data.author}
              type="user"
              title={t('Creator')}
              pictureUrl={
                apiData.data.author.picture &&
                apiData.data.author.picture.thumbnailUrl
              }
              name={`${apiData.data.author.firstName} ${apiData.data.author.lastName}`}
              colorPool={apiData.data.colorPool}
            />
            {apiData?.data?.attachments &&
              apiData?.data?.attachments.length > 0 && (
                <CardContainer>
                  <ExoAttachments attachments={apiData.data.attachments} />
                </CardContainer>
              )}

            {apiData?.data?.mailLogs && apiData?.data?.mailLogs.length > 0 && (
              <MailLogCard span={2} logs={apiData.data.mailLogs} />
            )}

            {apiData.data.archivedVersions?.length > 0 && (
              <CardContainer span={2} className="max-h-[200px]">
                <Box className="flex items-center gap-1 mb-1">
                  <HistoryIcon sx={{ fontSize: 14 }} />
                  <Typography variant="caption" fontWeight="bold">
                    {t('Version History')}
                  </Typography>
                </Box>
                <Box className="flex flex-col w-full overflow-auto overflow-x-hidden">
                  {apiData.data.archivedVersions
                    .sort((a, b) => b.revision - a.revision)
                    .map((version, index) => (
                      <Box key={version.id}>
                        <Box
                          className="flex items-center justify-between py-1 cursor-pointer hover:opacity-70"
                          onClick={() => navigate(`/orders/${version.id}`)}
                        >
                          <Box className="flex items-center gap-2">
                            <Typography
                              variant="caption"
                              fontWeight="bold"
                              className="monospace"
                            >
                              {version.nr}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {version.recipient?.name ||
                                version.recipient?.nameShort}
                            </Typography>
                          </Box>
                          <Box className="flex items-center gap-2">
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              className="monospace"
                            >
                              <DateLabel date={version.date} />
                            </Typography>
                            <Typography variant="caption" className="monospace">
                              <MoneyLabel money={version.bruttoAmount} />
                            </Typography>
                          </Box>
                        </Box>
                        {index < apiData.data.archivedVersions.length - 1 && (
                          <Divider />
                        )}
                      </Box>
                    ))}
                </Box>
              </CardContainer>
            )}

            {apiData.data?.sourceType === 'offer' &&
              apiData.data?.source?.id && (
                <Button
                  onClick={() => navigate(`/offers/${apiData.data.source.id}`)}
                  className="flex justify-center items-center gap-1 backdrop-blur-lg "
                  variant="contained"
                  color="secondary"
                >
                  <OpenInNewIcon />
                  <Typography>{t('Go to Offer')}</Typography>
                </Button>
              )}

            {apiData?.data?.invoices && apiData?.data?.invoices.length > 0 && (
              <CardContainer span={2}>
                <Typography variant="h6">{t('Invoices')}</Typography>
                <Box className="flex flex-col gap-2 w-full">
                  {apiData.data.invoices.map((invoice) => (
                    <Box
                      key={invoice.id}
                      className="flex justify-between items-center gap-4 px-2 py-1 rounded cursor-pointer"
                      onClick={() => navigate(`/invoices/${invoice.id}`)}
                      sx={{
                        '&:hover': {
                          backgroundColor: colors.selected,
                        },
                      }}
                    >
                      <Typography className="monospace whitespace-nowrap">
                        {invoice.nr ?? t('Draft')}
                      </Typography>
                      <Typography className="flex-1 truncate">
                        {invoice.title}
                      </Typography>
                      <MoneyLabel money={invoice.amount} />
                    </Box>
                  ))}
                </Box>
              </CardContainer>
            )}

            <CardContainer
              sx={{
                height: '100vh-600px',
                display: 'block',
                padding: '0px',
              }}
              span={99}
            >
              <DisplayPositions
                positions={apiData.data.positions}
                showSum={false}
                columns={
                  apiData.data.isDraft
                    ? columns
                    : [...columns, ...additionalColumns]
                }
              />
            </CardContainer>

            {apiData.data.note && (
              <CardContainer span={99}>
                <StyledTextContainer
                  backgroundColor="transparent"
                  fullWidth
                  color={colors.text}
                >
                  <RenderHTML HTML={apiData.data.note} />
                </StyledTextContainer>
              </CardContainer>
            )}
            {apiData.data.internalNote && (
              <CardContainer span={99}>
                <StyledTextContainer
                  backgroundColor="transparent"
                  fullWidth
                  color={colors.text}
                >
                  <RenderHTML HTML={apiData.data.internalNote} />
                </StyledTextContainer>
              </CardContainer>
            )}
          </CardGrid>
        </>
      )}
      <NewOrder
        open={open}
        onUpdate={handleUpdate}
        onClose={() => setOpen(false)}
        startData={startData}
        type={Boolean(startData) ? 'custom' : null}
      />
      <PopUpContainer open={openShare} onClose={handleCloseShare}>
        <Share
          title={`${t('Share Order')} ${apiData && apiData.data.nr}`}
          url={
            apiData && apiData.data.file ? apiData.data.file.temporaryUrl : ''
          }
        />
      </PopUpContainer>
      <Feedback setState={setAlertState} state={alertState} />
    </Box>
  );
};

const OrderSkeleton = () => {
  return (
    <CardGrid>
      <SkeletonCard height={54} span={4} />
      <SkeletonCard height={188} />
      <SkeletonCard height={188} />
      <SkeletonCard height={188} />
      <SkeletonCard height={188} />
      <SkeletonCard height={188} />
      <SkeletonCard height={120} span={4} />
    </CardGrid>
  );
};

export default ExtendOrder;
